import React, { useState } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { ButtonGroup } from '@atlaskit/button';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';
import { BreadcrumbRenderer } from '@atlassian/jira-breadcrumbs';
import PageHeader from '@atlassian/jira-common-components-page-header';
import { useExperienceFail } from '@atlassian/jira-experience-tracker';
import Graph from '@atlassian/jira-graph';
import type { header_softwareReleasesVersionDetail$key } from '@atlassian/jira-relay/src/__generated__/header_softwareReleasesVersionDetail.graphql';
import { isDevopsFeatureDisabledInFedRamp } from '@atlassian/jira-software-devops-fedramp-utils';
import AsyncReleaseAutomationMenu from '@atlassian/jira-software-releases-automation-menu/src/async';
import { ManageWarningsModal } from '@atlassian/jira-software-releases-manage-warnings';
import { NonCriticalErrorBoundary } from '../../common/utils/non-critical-error-boundary';
import { ConnectApps } from './connect-apps';
import { CONNECT_APP_EXPERIENCE_ID } from './connect-apps/constants';
import { AsyncEditableName } from './editable-name/async';
import { FeedbackButton } from './feedback-button';
import { ReleaseNotesButton } from './release-notes-button';

export const Header = ({
	jiraVersion,
}: {
	jiraVersion: header_softwareReleasesVersionDetail$key;
}) => {
	const breadcrumbs = <BreadcrumbRenderer />;

	const onConnectAppExpFail = useExperienceFail({ experience: CONNECT_APP_EXPERIENCE_ID });
	const [warningsModalOpen, setWarningsModalOpen] = useState(false);
	const [isEditing, setIsEditing] = useState(false);

	const version = useFragment(
		graphql`
			fragment header_softwareReleasesVersionDetail on JiraVersion {
				id @required(action: THROW)
				allIssues: issues(filter: ALL) @required(action: THROW) {
					totalCount @required(action: THROW)
				}
				...async_softwareReleasesVersionDetail_AsyncEditableName
				...ui_softwareReleasesManageWarnings_ManageWarningsModal
				...connectApps_softwareReleasesVersionDetail
				...releaseNotesButton_softwareReleasesVersionDetail
			}
		`,
		jiraVersion,
	);

	const issues = version.allIssues.totalCount;

	return (
		<PageHeader breadcrumbs={breadcrumbs} truncateTitle disableTitleStyles>
			<HeaderTitleLane>
				<LeftContainer>
					<HeaderTitle data-testid="software-releases-version-detail.ui.header.title">
						<PageTitleContainer isOverflowEnabled={isEditing}>
							<AsyncEditableName
								isEditing={isEditing}
								setIsEditing={setIsEditing}
								jiraVersion={version}
							/>
						</PageTitleContainer>
					</HeaderTitle>
				</LeftContainer>
				<RightContainer>
					<ButtonGroup>
						<Graph from={version.id} />
						<NonCriticalErrorBoundary fallback="flag" eventLocation="feedbackButton">
							<FeedbackButton />
						</NonCriticalErrorBoundary>
						<NonCriticalErrorBoundary fallback="unmount" eventLocation="releaseAutomationMenu">
							<AsyncReleaseAutomationMenu />
						</NonCriticalErrorBoundary>
						<ModalTransition>
							{warningsModalOpen && (
								<NonCriticalErrorBoundary fallback="flag" eventLocation="warningConfigModal">
									<ManageWarningsModal
										jiraVersion={version}
										setModalClosed={() => {
											setWarningsModalOpen(false);
										}}
									/>
								</NonCriticalErrorBoundary>
							)}
						</ModalTransition>
						{!isDevopsFeatureDisabledInFedRamp() && issues > 0 && (
							<ReleaseNotesButton jiraVersion={version} />
						)}
					</ButtonGroup>
				</RightContainer>
			</HeaderTitleLane>
			<NonCriticalErrorBoundary
				fallback="flag"
				eventLocation="headerConnectApps"
				onError={onConnectAppExpFail}
			>
				<MarketPlaceAddonLane>
					<ConnectApps jiraVersion={version} />
				</MarketPlaceAddonLane>
			</NonCriticalErrorBoundary>
		</PageHeader>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderTitleLane = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	rowGap: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LeftContainer = styled.div({
	flexGrow: 1,
	marginLeft: token('space.negative.100', '-8px'),
	minWidth: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RightContainer = styled.div({
	flexGrow: 1,
	display: 'flex',
	justifyContent: 'flex-end',
	minWidth: '300px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MarketPlaceAddonLane = styled.div({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-end',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderTitle = styled.div({
	display: 'flex',
	alignItems: 'center',
	minWidth: 0,
	height: '42px',
});

type PageTitleContainerProps = {
	isOverflowEnabled: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PageTitleContainer = styled.div({
	marginTop: token('space.negative.100', '-8px'),
	minWidth: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	overflow: (props: PageTitleContainerProps) => (props.isOverflowEnabled ? 'visible' : 'hidden'),
});
